import { supabase } from './supabase'
import { apiFetch, isApiDestination } from '../i18n/apiFetch'
import { ApiError } from '../api/client'

/**
 * The live Supabase session as the COLLECTION OWNER {userId, token}.
 *
 *   { ok: true, userId: null, token: null } - a guest: no session, or an
 *                                              ANONYMOUS session (guests send
 *                                              no Authorization at all)
 *   { ok: true, userId, token }             - a permanent signed-in user (token
 *                                              may be '' if the session
 *                                              carries none)
 *   { ok: false }                            - the read failed or threw; who
 *                                              is signed in is UNKNOWN
 *
 * With `fresh`, a token within five minutes of expiry is refreshed first, so
 * a long import does not start on a token about to die mid-request.
 */
export type SessionIdentity =
  | { ok: true; userId: string | null; token: string | null }
  | { ok: false }

const REFRESH_MARGIN_MS = 5 * 60_000

type SessionLike = {
  access_token?: string
  expires_at?: number
  user?: { id?: string; is_anonymous?: boolean } | null
} | null | undefined

function ownerOf(session: SessionLike): SessionIdentity {
  if (!session?.user?.id || session.user.is_anonymous) return { ok: true, userId: null, token: null }
  return { ok: true, userId: session.user.id, token: session.access_token ?? '' }
}

export async function readSessionIdentity(opts?: { fresh?: boolean }): Promise<SessionIdentity> {
  if (!supabase) return { ok: true, userId: null, token: null }
  try {
    const { data, error } = await supabase.auth.getSession()
    if (error) return { ok: false }
    const session = data?.session as SessionLike
    const owner = ownerOf(session)
    if (opts?.fresh && owner.ok && owner.userId && typeof session?.expires_at === 'number'
        && session.expires_at * 1000 - Date.now() < REFRESH_MARGIN_MS) {
      const fresh = await refreshSessionIdentity()
      // A refresh that failed while the old token is still valid keeps it; a
      // refresh that came back as someone else reports that someone else.
      if (fresh.ok) return fresh
    }
    return owner
  } catch {
    return { ok: false }
  }
}

/** Force a token refresh (after a 401) and report whose it is. */
export async function refreshSessionIdentity(): Promise<SessionIdentity> {
  if (!supabase) return { ok: true, userId: null, token: null }
  try {
    const { data, error } = await supabase.auth.refreshSession()
    if (error || !data?.session) return { ok: false }
    return ownerOf(data.session as SessionLike)
  } catch {
    return { ok: false }
  }
}

/**
 * What a collection WRITE may send, for the owner the operation STARTED under.
 *
 *   { ok: true, headers, userId, token }
 *                          - send these headers: `Authorization: Bearer <token>`
 *                            for that permanent user, or nothing at all for a
 *                            guest (expectedUserId null).
 *   { ok: false }          - refuse to send. The session could not be read, its
 *                            token is missing, OR it now belongs to someone
 *                            other than `expectedUserId` (an account switch,
 *                            a sign-in or a sign-out mid-operation). The
 *                            caller fails with a retryable sign_in_expired and
 *                            makes NO request: a write must never go out as a
 *                            different user than the one who started it.
 *
 * The bearer goes ONLY to our API (isApiDestination, the same gate as the
 * interface-language header), never to any other origin.
 */
export type WriteAuth =
  | { ok: true; headers: Record<string, string>; userId: string | null; token: string | null }
  | { ok: false }

function bearerHeaders(token: string): Record<string, string> {
  // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
  return { Authorization: `Bearer ${token}` }
}

export async function writeAuthFor(url: string, expectedUserId: string | null): Promise<WriteAuth> {
  const live = await readSessionIdentity({ fresh: true })
  if (!live.ok) return { ok: false }
  if (live.userId !== expectedUserId) return { ok: false }
  if (expectedUserId === null) return { ok: true, headers: {}, userId: null, token: null }
  if (!live.token) return { ok: false }
  // An account write to a destination not recognized as our API is refused,
  // never sent bearer-less: the owner guard would read it as someone else's.
  if (!isApiDestination(url)) return { ok: false }
  return { ok: true, headers: bearerHeaders(live.token), userId: live.userId, token: live.token }
}

/**
 * Send ONE collection request as `expectedUserId`, with the shared 401 rule:
 * on a 401, refresh the session once and resend the IDENTICAL request (same
 * body, so the same op_key) with the new token, but only if the refreshed
 * session is still that user. A second 401 comes back to the caller, which
 * shows "sign in again". 403, 429, 5xx and network failures are returned or
 * thrown untouched: they are never an auth failure.
 *
 * Returns null when nothing was sent (the session is not that user, or its
 * token could not be had).
 */
export async function fetchAsCollectionOwner(
  url: string,
  init: RequestInit,
  expectedUserId: string | null,
): Promise<Response | null> {
  const auth = await writeAuthFor(url, expectedUserId)
  if (!auth.ok || auth.userId !== expectedUserId) return null
  const send = (headers: Record<string, string>) =>
    apiFetch(url, { ...init, headers: { ...(init.headers as Record<string, string> | undefined), ...headers } })
  const first = await send(auth.headers)
  if (first.status !== 401 || expectedUserId === null) return first
  const fresh = await refreshSessionIdentity()
  if (!fresh.ok || fresh.userId !== expectedUserId || !fresh.token) return first
  return send(bearerHeaders(fresh.token))
}

/** Thrown when an account call cannot be made as the account it is for. */
export class CollectionAuthError extends Error {
  constructor() {
    // eslint-disable-next-line i18next/no-literal-string -- developer error, never shown
    super('collection request not sent: the session is not the collection owner')
    // eslint-disable-next-line i18next/no-literal-string -- error class name
    this.name = 'CollectionAuthError'
  }
}

/**
 * The same 401 rule for the ApiError-throwing helpers (claim, owned-names):
 * call with `ownerId`'s fresh token; on a 401, refresh once and call again
 * with the new token if the session is still `ownerId`. Throws
 * CollectionAuthError when no token for that owner can be had (nothing sent).
 */
export async function withCollectionToken<T>(ownerId: string, call: (token: string) => Promise<T>): Promise<T> {
  const live = await readSessionIdentity({ fresh: true })
  if (!live.ok || live.userId !== ownerId || !live.token) throw new CollectionAuthError()
  try {
    return await call(live.token)
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 401)) throw e
    const fresh = await refreshSessionIdentity()
    if (!fresh.ok || fresh.userId !== ownerId || !fresh.token) throw new CollectionAuthError()
    return call(fresh.token)
  }
}

/** True for a failure that means "sign in again": a second 401, or no token. */
export function isCollectionAuthFailure(e: unknown): boolean {
  return e instanceof CollectionAuthError || (e instanceof ApiError && e.status === 401)
}
